import { Search as SearchIcon } from "@mui/icons-material";
import Logger from "../../../services/Logger";
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    Divider,
    Grid,
    Pagination,
    Paper,
    TextField,
    Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import CambridgeService from "./CambridgeService";

const CambridgeMain = () => {
    const [word, setWord] = useState("");
    const [definition, setDefinition] = useState(null);
    const [searchResults, setSearchResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalResults, setTotalResults] = useState(0);
    const [searchData, setSearchData] = useState(null);
    const [nearbyEntries, setNearbyEntries] = useState([]);
    const [spellingSuggestions, setSpellingSuggestions] = useState([]);
    const [wordOfTheDay, setWordOfTheDay] = useState(null);
    const [wordOfTheDayLoading, setWordOfTheDayLoading] = useState(false);
    const [wordOfTheDayError, setWordOfTheDayError] = useState(null);
    const [wordOfTheDayPreview, setWordOfTheDayPreview] = useState(null);
    const [showPreview, setShowPreview] = useState(true);

    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    // Initialize from URL parameters
    useEffect(() => {
        const urlWord = searchParams.get('q');
        const urlPage = searchParams.get('pageindex');
        const urlPageSize = searchParams.get('pagesize');

        if (urlWord) {
            setWord(urlWord);
        }
        if (urlPage && !isNaN(parseInt(urlPage))) {
            setCurrentPage(parseInt(urlPage));
        }
        if (urlPageSize && !isNaN(parseInt(urlPageSize))) {
            setPageSize(parseInt(urlPageSize));
        }

        // Auto-search if word is in URL
        if (urlWord && urlWord.trim()) {
            const pageIndex = urlPage && !isNaN(parseInt(urlPage)) ? parseInt(urlPage) : 1;
            const pageSizeParam = urlPageSize && !isNaN(parseInt(urlPageSize)) ? parseInt(urlPageSize) : 10;
            fetchDefinition(pageIndex, pageSizeParam);
        }
    }, [searchParams]);

    // Fetch word of the day on component mount
    useEffect(() => {
        fetchWordOfTheDay();
    }, []);

    // Update URL when search parameters change
    const updateURL = (searchWord, pageIndex, pageSizeParam) => {
        const params = new URLSearchParams();
        if (searchWord) params.set('q', searchWord);
        if (Number(pageIndex) > 1) params.set('pageindex', Number(pageIndex).toString());
        if (Number(pageSizeParam) !== 10) params.set('pagesize', Number(pageSizeParam).toString());
        
        navigate(`?${params.toString()}`, { replace: true });
    };

    const fetchWordOfTheDay = async () => {
        setWordOfTheDayLoading(true);
        setWordOfTheDayError(null);

        try {
            // Fetch both the full word of the day and preview
            const [wordOfTheDayData, previewData] = await Promise.all([
                CambridgeService.getWordOfTheDay(),
                CambridgeService.getWordOfTheDayPreview()
            ]);
            
            setWordOfTheDay(wordOfTheDayData);
            setWordOfTheDayPreview(previewData);
        } catch (err) {
            Logger.error("Word of the day API Error:", err);
            setWordOfTheDayError(`Failed to fetch word of the day: ${err.message}`);
        } finally {
            setWordOfTheDayLoading(false);
        }
    };

    const fetchDefinition = async (pageIndex = currentPage, pageSizeParam = pageSize) => {
        if (!word.trim()) {
            setError("Please enter a word to search");
            return;
        }

        setLoading(true);
        setError(null);
        setDefinition(null);
        setSearchResults([]);

        try {
            // Ensure pageIndex and pageSizeParam are numbers
            const numericPageIndex = Number(pageIndex) || 1;
            const numericPageSize = Number(pageSizeParam) || 10;

            // Update URL with current search parameters
            updateURL(word.trim(), numericPageIndex, numericPageSize);

            // Search for the word using service
            const searchData = await CambridgeService.searchWords(word.trim(), numericPageIndex, numericPageSize);

            if (!searchData.results || searchData.results.length === 0) {
                // Try to get spelling suggestions
                try {
                    const suggestionsData = await CambridgeService.getSpellingSuggestions(word.trim());
                    if (suggestionsData && suggestionsData.results && suggestionsData.results.length > 0) {
                        setSpellingSuggestions(suggestionsData.results);
                        throw new Error("No results found for this word. Did you mean one of these?");
                    }
                } catch (suggestionError) {
                    Logger.warn("Failed to fetch spelling suggestions:", suggestionError);
                }
                throw new Error("No results found for this word");
            }

            // Store search results and pagination info
            setSearchResults(searchData.results);
            setSearchData(searchData);
            setTotalResults(searchData.resultNumber || searchData.results.length);
            setCurrentPage(searchData.pageNumber || numericPageIndex);

            // Get the first result's entry ID
            const entryId = searchData.results[0].entryId;

            // Get detailed definition using service
            const detailData = await CambridgeService.getWordDefinition(entryId);

            if (detailData) {
                setDefinition({
                    ...detailData,
                    searchResult: searchData.results[0]
                });
                
                // Fetch nearby entries (alphabetically adjacent words)
                try {
                    const nearbyData = await CambridgeService.getNearbyEntries(entryId);
                    if (nearbyData && nearbyData.nearbyEntries) {
                        setNearbyEntries(nearbyData.nearbyEntries);
                    }
                } catch (nearbyError) {
                    Logger.warn("Failed to fetch nearby entries:", nearbyError);
                    setNearbyEntries([]);
                }
            } else {
                throw new Error("No definition found for this word");
            }
        } catch (err) {
            Logger.error("Cambridge API Error:", err);
            setError(`Failed to fetch definition: ${err.message}`);
        } finally {
            setLoading(false);
        }
    };

    const handleKeyPress = (event) => {
        if (event.key === "Enter") {
            fetchDefinition();
        }
    };


    const clearSearch = () => {
        setWord("");
        setDefinition(null);
        setError(null);
        setSearchResults([]);
        setSearchData(null);
        setCurrentPage(1);
        setTotalResults(0);
        setNearbyEntries([]);
        setSpellingSuggestions([]);
        navigate("/admin/system/integrate/cambridge", { replace: true });
    };

    const handleWordOfTheDayClick = () => {
        if (wordOfTheDay && wordOfTheDay.entryLabel) {
            setWord(wordOfTheDay.entryLabel);
            setCurrentPage(1);
            fetchDefinition(1, pageSize);
        }
    };

    const togglePreview = () => {
        setShowPreview(!showPreview);
    };

    const handlePageChange = (event, page) => {
        const numericPage = Number(page);
        setCurrentPage(numericPage);
        fetchDefinition(numericPage, Number(pageSize));
    };

    const handlePageSizeChange = (event) => {
        const newPageSize = Number(event.target.value);
        setPageSize(newPageSize);
        setCurrentPage(1);
        fetchDefinition(1, newPageSize);
    };

    const handleResultClick = async (result) => {
        setLoading(true);
        setError(null);
        setDefinition(null);

        try {
            const detailData = await CambridgeService.getWordDefinition(result.entryId);

            if (detailData) {
                setDefinition({
                    ...detailData,
                    searchResult: result
                });
                
                // Fetch nearby entries (alphabetically adjacent words)
                try {
                    const nearbyData = await CambridgeService.getNearbyEntries(result.entryId);
                    if (nearbyData && nearbyData.nearbyEntries) {
                        setNearbyEntries(nearbyData.nearbyEntries);
                    }
                } catch (nearbyError) {
                    Logger.warn("Failed to fetch nearby entries:", nearbyError);
                    setNearbyEntries([]);
                }
            } else {
                throw new Error("No definition found for this entry");
            }
        } catch (err) {
            Logger.error("Cambridge API Error:", err);
            setError(`Failed to fetch definition: ${err.message}`);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box sx={{ maxWidth: 'lg', mx: 'auto', mt: 4, mb: 4, px: 3 }}>
            <Typography variant="h4" component="h1" gutterBottom>
                Cambridge Dictionary API
            </Typography>

            <Grid container spacing={3}>
                {/* Word of the Day Section */}
                <Grid size={{ xs: 12 }}>
                    <Paper sx={{ p: 3, mb: 3 }}>
                        <Typography variant="h6" gutterBottom sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            📚 Today's Word
                        </Typography>
                        
                        {wordOfTheDayLoading && (
                            <Box sx={{ display: "flex", alignItems: "center", gap: 2, py: 2 }}>
                                <CircularProgress size={24} />
                                <Typography variant="body2" color="text.secondary">
                                    Loading today's word...
                                </Typography>
                            </Box>
                        )}

                        {wordOfTheDayError && (
                            <Alert severity="error" sx={{ mb: 2 }}>
                                {wordOfTheDayError}
                                <Button 
                                    variant="outlined" 
                                    size="small" 
                                    onClick={fetchWordOfTheDay}
                                    sx={{ ml: 2 }}
                                >
                                    Retry
                                </Button>
                            </Alert>
                        )}

                        {wordOfTheDay && !wordOfTheDayLoading && (
                            <Card 
                                sx={{ 
                                    cursor: "pointer", 
                                    transition: "all 0.2s ease-in-out",
                                    "&:hover": {
                                        transform: "translateY(-2px)",
                                        boxShadow: 3
                                    }
                                }}
                                onClick={handleWordOfTheDayClick}
                            >
                                <CardContent>
                                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 2 }}>
                                        <Typography variant="h5" component="h2" sx={{ fontWeight: "bold", color: "primary.main" }}>
                                            {wordOfTheDay.entryLabel}
                                        </Typography>
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                            <Typography variant="body2" color="text.secondary">
                                                {new Date().toLocaleDateString()}
                                            </Typography>
                                            <Button
                                                variant="outlined"
                                                size="small"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    togglePreview();
                                                }}
                                                sx={{ ml: 1, minWidth: "auto", px: 1 }}
                                            >
                                                {showPreview ? "Full" : "Preview"}
                                            </Button>
                                        </Box>
                                    </Box>

                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                        Dictionary: {wordOfTheDay.dictionaryCode}
                                    </Typography>

                                    {/* Content based on toggle */}
                                    {showPreview && wordOfTheDayPreview ? (
                                        <Box sx={{ mb: 2 }}>
                                            <Typography variant="subtitle2" gutterBottom>
                                                Preview:
                                            </Typography>
                                            <Box 
                                                sx={{ 
                                                    border: "1px solid #e0e0e0", 
                                                    borderRadius: 1, 
                                                    p: 2, 
                                                    backgroundColor: "#fafafa",
                                                    maxHeight: "200px",
                                                    overflow: "auto"
                                                }}
                                                dangerouslySetInnerHTML={{ __html: wordOfTheDayPreview.htmlEntryPreview }}
                                            />
                                            {wordOfTheDayPreview.textEntryPreview && (
                                                <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontStyle: "italic" }}>
                                                    {wordOfTheDayPreview.textEntryPreview}
                                                </Typography>
                                            )}
                                        </Box>
                                    ) : (
                                        <>
                                            {/* Topics */}
                                            {wordOfTheDay.topics && wordOfTheDay.topics.length > 0 && (
                                                <Box sx={{ mb: 2 }}>
                                                    <Typography variant="subtitle2" gutterBottom>
                                                        Topics:
                                                    </Typography>
                                                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                                                        {wordOfTheDay.topics.slice(0, 5).map((topic, index) => (
                                                            <Button
                                                                key={index}
                                                                variant="outlined"
                                                                size="small"
                                                                sx={{ textTransform: "none", fontSize: "0.75rem" }}
                                                            >
                                                                {topic.topicLabel}
                                                            </Button>
                                                        ))}
                                                    </Box>
                                                </Box>
                                            )}
                                        </>
                                    )}

                                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
                                        Click to explore this word in detail
                                    </Typography>
                                </CardContent>
                            </Card>
                        )}
                    </Paper>
                </Grid>

                {/* Search Section */}
                <Grid size={{ xs: 12 }}>
                    <Paper sx={{ p: 3 }}>
                        <Typography variant="h6" gutterBottom>
                            Word Search
                        </Typography>

                        <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
                            <TextField
                                fullWidth
                                label="Enter a word"
                                value={word}
                                onChange={(e) => setWord(e.target.value)}
                                onKeyPress={handleKeyPress}
                                disabled={loading}
                                placeholder="Type a word and press Enter or click Search"
                            />
                            <Button
                                variant="contained"
                                onClick={fetchDefinition}
                                disabled={loading || !word.trim()}
                                startIcon={loading ? <CircularProgress size={20} /> : <SearchIcon />}
                                sx={{ minWidth: 120 }}
                            >
                                {loading ? "Searching..." : "Search"}
                            </Button>
                            <Button
                                variant="outlined"
                                onClick={clearSearch}
                                disabled={loading}
                            >
                                Clear
                            </Button>
                        </Box>

                        {error && (
                            <Alert severity="error" sx={{ mb: 2 }}>
                                {error}
                                {spellingSuggestions.length > 0 && (
                                    <Box sx={{ mt: 2 }}>
                                        <Typography variant="subtitle2" gutterBottom>
                                            Did you mean:
                                        </Typography>
                                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                                            {spellingSuggestions.map((suggestion, index) => (
                                                <Button
                                                    key={index}
                                                    variant="outlined"
                                                    size="small"
                                                    onClick={() => {
                                                        setWord(suggestion.entryLabel);
                                                        setSpellingSuggestions([]);
                                                        setTimeout(() => {
                                                            fetchDefinition();
                                                        }, 100);
                                                    }}
                                                    sx={{ 
                                                        textTransform: "none",
                                                        fontSize: "0.875rem"
                                                    }}
                                                >
                                                    {suggestion.entryLabel}
                                                </Button>
                                            ))}
                                        </Box>
                                    </Box>
                                )}
                            </Alert>
                        )}

                        {/* Search Results */}
                        {searchResults.length > 0 && !definition && (
                            <Card sx={{ mt: 2 }}>
                                <CardContent>
                                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                                        <Typography variant="h6">
                                            Search Results ({totalResults} total)
                                        </Typography>
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                                            <Typography variant="body2">Results per page:</Typography>
                                            <TextField
                                                select
                                                size="small"
                                                value={pageSize}
                                                onChange={handlePageSizeChange}
                                                sx={{ minWidth: 80 }}
                                                SelectProps={{
                                                    native: true,
                                                }}
                                            >
                                                <option value={5}>5</option>
                                                <option value={10}>10</option>
                                                <option value={20}>20</option>
                                                <option value={50}>50</option>
                                            </TextField>
                                        </Box>
                                    </Box>
                                    
                                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                                        {searchResults.map((result, index) => (
                                            <Button
                                                key={index}
                                                variant="outlined"
                                                onClick={() => handleResultClick(result)}
                                                disabled={loading}
                                                sx={{ 
                                                    justifyContent: "flex-start", 
                                                    textTransform: "none",
                                                    textAlign: "left",
                                                    p: 2
                                                }}
                                            >
                                                <Box>
                                                    <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                                                        {result.entryLabel}
                                                    </Typography>
                                                    <Typography variant="body2" color="text.secondary">
                                                        {result.entryId}
                                                    </Typography>
                                                </Box>
                                            </Button>
                                        ))}
                                    </Box>

                                    {/* Pagination */}
                                    {searchData && typeof searchData.pageNumber === 'number' && (
                                        <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
                                            <Pagination
                                                count={Math.ceil(Number(totalResults) / Number(pageSize))}
                                                page={Number(currentPage)}
                                                onChange={handlePageChange}
                                                color="primary"
                                                disabled={loading}
                                            />
                                        </Box>
                                    )}
                                </CardContent>
                            </Card>
                        )}

                        {definition && (
                            <Card sx={{ mt: 2 }}>
                                <CardContent>
                                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                                        <Typography variant="h5" component="h2">
                                            {definition.entryLabel || word}
                                        </Typography>
                                        {searchResults.length > 0 && (
                                            <Button
                                                variant="outlined"
                                                size="small"
                                                onClick={() => {
                                                    setDefinition(null);
                                                }}
                                            >
                                                Back to Results
                                            </Button>
                                        )}
                                    </Box>

                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                        Dictionary: {definition.dictionaryCode}
                                    </Typography>

                                    <Divider sx={{ my: 2 }} />

                                    {/* Topics */}
                                    {definition.topics && definition.topics.length > 0 && (
                                        <Box sx={{ mb: 3 }}>
                                            <Typography variant="h6" gutterBottom>
                                                Topics
                                            </Typography>
                                            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                                                {definition.topics.slice(0, 10).map((topic, index) => (
                                                    <Button
                                                        key={index}
                                                        variant="outlined"
                                                        size="small"
                                                        sx={{ textTransform: "none", fontSize: "0.75rem" }}
                                                    >
                                                        {topic.topicLabel}
                                                    </Button>
                                                ))}
                                            </Box>
                                        </Box>
                                    )}

                                    <Divider sx={{ my: 2 }} />

                                    {/* Entry Content */}
                                    <Typography variant="h6" gutterBottom>
                                        Definition
                                    </Typography>
                                    <Box 
                                        sx={{ 
                                            border: "1px solid #e0e0e0", 
                                            borderRadius: 1, 
                                            p: 2, 
                                            backgroundColor: "#fafafa",
                                            maxHeight: "400px",
                                            overflow: "auto"
                                        }}
                                        dangerouslySetInnerHTML={{ __html: definition.entryContent }}
                                    />

                                    {/* Entry URL */}
                                    {definition.entryUrl && (
                                        <Box sx={{ mt: 2 }}>
                                            <Typography variant="body2" color="text.secondary">
                                                Source: <a href={definition.entryUrl} target="_blank" rel="noopener noreferrer">
                                                    Cambridge Dictionary
                                                </a>
                                            </Typography>
                                        </Box>
                                    )}

                                    {/* Nearby Entries */}
                                    {nearbyEntries && nearbyEntries.length > 0 && (
                                        <Box sx={{ mt: 3 }}>
                                            <Divider sx={{ mb: 2 }} />
                                            <Typography variant="h6" gutterBottom>
                                                Nearby Words
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                                Alphabetically adjacent words
                                            </Typography>
                                            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                                                {nearbyEntries.map((nearbyEntry, index) => (
                                                    <Button
                                                        key={index}
                                                        variant="outlined"
                                                        size="small"
                                                        onClick={() => handleResultClick({
                                                            entryId: nearbyEntry.entryId,
                                                            entryLabel: nearbyEntry.entryLabel,
                                                            entryUrl: nearbyEntry.entryUrl
                                                        })}
                                                        sx={{ 
                                                            textTransform: "none",
                                                            fontSize: "0.875rem"
                                                        }}
                                                    >
                                                        {nearbyEntry.entryLabel}
                                                    </Button>
                                                ))}
                                            </Box>
                                        </Box>
                                    )}
                                </CardContent>
                            </Card>
                        )}
                    </Paper>
                </Grid>
            </Grid>
        </Box>
    );
};

export default CambridgeMain;
